import { supabase } from './supabase';
import { TimeEntry, MonthlyRate, MonthlySummary, TaxEarnings, TaxExpense, TaxExpenseInput, ExpenseCategory } from '../types/database';
import { convertLondonTimeToUTC, convertUTCToLondonTime, parseDateString } from '../utils/dateUtils';

export class SupabaseService {
  // Helper method to wait for auth to be ready
  private async waitForAuth(): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (!session) throw new Error('User not authenticated');
    return session.user.id;
  }

  // Time Entries CRUD operations
  async insertTimeEntry(entry: Omit<TimeEntry, 'id' | 'created_at' | 'updated_at'>): Promise<number> {
    const userId = await this.waitForAuth();

    // Convert London times to UTC for storage
    const dateObj = parseDateString(entry.date);
    const shiftsForStorage = entry.shifts.map(shift => ({
      ...shift,
      clockInUTC: convertLondonTimeToUTC(shift.clockIn, dateObj),
      clockOutUTC: convertLondonTimeToUTC(shift.clockOut, dateObj)
    }));

    const { data, error } = await supabase
      .from('time_entries')
      .insert({
        user_id: userId,
        date: entry.date,
        shifts: shiftsForStorage,
        total_hours: entry.totalHours
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to insert time entry:', error);
      throw error;
    }

    return data.id;
  }

  async getTimeEntriesByMonth(year: number, month: number): Promise<TimeEntry[]> {
    const userId = await this.waitForAuth();

    const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
    // Get the last day of the month properly
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
    
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) {
      console.error('Failed to get time entries:', error);
      throw error;
    }

    return data.map(row => {
      // Convert UTC times back to London times for display
      const shiftsForDisplay = row.shifts.map((shift: any) => {
        // Handle both old format (without UTC) and new format (with UTC)
        if (shift.clockInUTC && shift.clockOutUTC) {
          return {
            ...shift,
            clockIn: convertUTCToLondonTime(shift.clockInUTC),
            clockOut: convertUTCToLondonTime(shift.clockOutUTC)
          };
        }
        // For existing data without UTC conversion, return as-is
        return shift;
      });

      return {
        id: row.id,
        date: row.date,
        shifts: shiftsForDisplay,
        totalHours: row.total_hours,
        created_at: row.created_at,
        updated_at: row.updated_at
      };
    });
  }

  async getTimeEntryByDate(date: string): Promise<TimeEntry | null> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .maybeSingle();

    if (error) {
      console.error('Failed to get time entry:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Convert UTC times back to London times for display
    const shiftsForDisplay = data.shifts.map((shift: any) => {
      // Handle both old format (without UTC) and new format (with UTC)
      if (shift.clockInUTC && shift.clockOutUTC) {
        return {
          ...shift,
          clockIn: convertUTCToLondonTime(shift.clockInUTC),
          clockOut: convertUTCToLondonTime(shift.clockOutUTC)
        };
      }
      // For existing data without UTC conversion, return as-is
      return shift;
    });

    return {
      id: data.id,
      date: data.date,
      shifts: shiftsForDisplay,
      totalHours: data.total_hours,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  async updateTimeEntry(id: number, entry: Partial<TimeEntry>): Promise<boolean> {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (entry.date) updateData.date = entry.date;
    if (entry.totalHours !== undefined) updateData.total_hours = entry.totalHours;
    
    if (entry.shifts) {
      // Convert London times to UTC for storage
      const dateObj = parseDateString(entry.date || '');
      const shiftsForStorage = entry.shifts.map(shift => ({
        ...shift,
        clockInUTC: convertLondonTimeToUTC(shift.clockIn, dateObj),
        clockOutUTC: convertLondonTimeToUTC(shift.clockOut, dateObj)
      }));
      updateData.shifts = shiftsForStorage;
    }

    const { error } = await supabase
      .from('time_entries')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Failed to update time entry:', error);
      return false;
    }

    return true;
  }

  async deleteTimeEntry(id: number): Promise<boolean> {
    const { error } = await supabase
      .from('time_entries')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Failed to delete time entry:', error);
      return false;
    }

    return true;
  }

  // Monthly Rates CRUD operations
  async insertOrUpdateMonthlyRate(rate: Omit<MonthlyRate, 'id' | 'created_at' | 'updated_at'>): Promise<number> {
    const userId = await this.waitForAuth();

    // Check if rate already exists for this year/month
    const { data: existingRate } = await supabase
      .from('monthly_rates')
      .select('id')
      .eq('user_id', userId)
      .eq('year', rate.year)
      .eq('month', rate.month)
      .maybeSingle();

    if (existingRate) {
      // Update existing rate
      const { data, error } = await supabase
        .from('monthly_rates')
        .update({
          hourly_rate: rate.hourlyRate,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingRate.id)
        .select('id')
        .single();

      if (error) {
        console.error('Failed to update monthly rate:', error);
        throw error;
      }

      return data.id;
    } else {
      // Insert new rate
      const { data, error } = await supabase
        .from('monthly_rates')
        .insert({
          user_id: userId,
          year: rate.year,
          month: rate.month,
          hourly_rate: rate.hourlyRate
        })
        .select('id')
        .single();

      if (error) {
        console.error('Failed to insert monthly rate:', error);
        throw error;
      }

      return data.id;
    }
  }

  async getMonthlyRate(year: number, month: number): Promise<MonthlyRate | null> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('monthly_rates')
      .select('*')
      .eq('user_id', userId)
      .eq('year', year)
      .eq('month', month)
      .maybeSingle();

    if (error) {
      console.error('Failed to get monthly rate:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return {
      id: data.id,
      year: data.year,
      month: data.month,
      hourlyRate: data.hourly_rate,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  // Summary operations
  async getMonthlySummary(year: number, month: number): Promise<MonthlySummary | null> {
    const rate = await this.getMonthlyRate(year, month);
    const entries = await this.getTimeEntriesByMonth(year, month);
    const totalHours = entries.reduce((sum, entry) => sum + (entry.totalHours || 0), 0);
    const daysWorked = entries.length;

    return {
      year,
      month,
      totalHours,
      hourlyRate: rate?.hourlyRate || 0,
      totalEarnings: totalHours * (rate?.hourlyRate || 0),
      daysWorked
    };
  }

  async getAllMonthlySummaries(): Promise<MonthlySummary[]> {
    const { data: entries, error: entriesError } = await supabase
      .from('time_entries')
      .select('*');

    if (entriesError) {
      console.error('Failed to get time entries:', entriesError);
      throw entriesError;
    }

    const { data: rates, error: ratesError } = await supabase
      .from('monthly_rates')
      .select('*');

    if (ratesError) {
      console.error('Failed to get monthly rates:', ratesError);
      throw ratesError;
    }

    const summaries = new Map<string, MonthlySummary>();

    entries.forEach(entry => {
      const date = parseDateString(entry.date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const key = `${year}-${month}`;

      if (!summaries.has(key)) {
        const rate = rates.find(r => r.year === year && r.month === month);
        summaries.set(key, {
          year,
          month,
          totalHours: 0,
          hourlyRate: rate?.hourly_rate || 0,
          totalEarnings: 0,
          daysWorked: 0
        });
      }

      const summary = summaries.get(key)!;
      summary.totalHours += (entry.total_hours || 0);
      summary.daysWorked += 1;
      summary.totalEarnings = summary.totalHours * summary.hourlyRate;
    });

    return Array.from(summaries.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }

  // Tax Earnings CRUD operations (JSONB format)
  async getTaxEarningsByTaxYear(taxYearStart: number): Promise<TaxEarnings | null> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_earnings')
      .select('*')
      .eq('user_id', userId)
      .eq('tax_year_start', taxYearStart)
      .maybeSingle();

    if (error) {
      console.error('Failed to get tax earnings:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return {
      id: data.id,
      tax_year_start: data.tax_year_start,
      monthly_earnings: data.monthly_earnings || {},
      has_other_job: data.has_other_job || false,
      other_job_salary: data.other_job_salary || 0,
      is_paye: data.is_paye || false,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  async getMonthlyEarningsFromTimeEntries(taxYearStart: number): Promise<Record<number, number>> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase.rpc('get_monthly_earnings_from_time_entries', {
      p_user_id: userId,
      p_tax_year_start: taxYearStart
    });

    if (error) {
      console.error('Failed to get monthly earnings from time entries:', error);
      throw error;
    }

    // Convert string keys to numbers
    const result: Record<number, number> = {};
    if (data) {
      Object.entries(data).forEach(([monthStr, amount]) => {
        const month = parseInt(monthStr, 10);
        if (!isNaN(month)) {
          result[month] = typeof amount === 'number' ? amount : parseFloat(amount as string) || 0;
        }
      });
    }

    return result;
  }

  async upsertTaxEarningsForYear(
    taxYearStart: number, 
    monthlyEarnings: Record<number, number>,
    hasOtherJob?: boolean,
    otherJobSalary?: number,
    isPaye?: boolean
  ): Promise<TaxEarnings> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_earnings')
      .upsert({
        user_id: userId,
        tax_year_start: taxYearStart,
        monthly_earnings: monthlyEarnings,
        has_other_job: hasOtherJob || false,
        other_job_salary: otherJobSalary || 0,
        is_paye: isPaye || false
      }, {
        onConflict: 'user_id,tax_year_start'
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to upsert tax earnings for year:', error);
      throw error;
    }

    return {
      id: data.id,
      tax_year_start: data.tax_year_start,
      monthly_earnings: data.monthly_earnings || {},
      has_other_job: data.has_other_job || false,
      other_job_salary: data.other_job_salary || 0,
      is_paye: data.is_paye || false,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  async getAllTaxEarnings(): Promise<TaxEarnings[]> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_earnings')
      .select('*')
      .eq('user_id', userId)
      .order('tax_year_start', { ascending: false });

    if (error) {
      console.error('Failed to get all tax earnings:', error);
      throw error;
    }

    return data.map(row => ({
      id: row.id,
      tax_year_start: row.tax_year_start,
      monthly_earnings: row.monthly_earnings || {},
      has_other_job: row.has_other_job || false,
      other_job_salary: row.other_job_salary || 0,
      is_paye: row.is_paye || false,
      created_at: row.created_at,
      updated_at: row.updated_at
    }));
  }

  // Tax expenses CRUD operations
  private mapTaxExpense(row: Record<string, unknown>): TaxExpense {
    return {
      id: row.id as number,
      tax_year_start: row.tax_year_start as number,
      month: row.month as number,
      name: row.name as string,
      amount: typeof row.amount === 'number' ? row.amount : parseFloat(row.amount as string) || 0,
      category: (row.category as ExpenseCategory) || 'other',
      created_at: row.created_at as string,
      updated_at: row.updated_at as string,
    };
  }

  async getTaxExpenses(taxYearStart: number): Promise<TaxExpense[]> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_expenses')
      .select('*')
      .eq('user_id', userId)
      .eq('tax_year_start', taxYearStart)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Failed to get tax expenses:', error);
      throw error;
    }

    return (data ?? []).map(row => this.mapTaxExpense(row));
  }

  async addTaxExpense(expense: TaxExpenseInput): Promise<TaxExpense> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_expenses')
      .insert({ ...expense, user_id: userId })
      .select()
      .single();

    if (error) {
      console.error('Failed to add tax expense:', error);
      throw error;
    }

    return this.mapTaxExpense(data);
  }

  async updateTaxExpense(id: number, expense: Partial<TaxExpenseInput>): Promise<TaxExpense> {
    const userId = await this.waitForAuth();

    const { data, error } = await supabase
      .from('tax_expenses')
      .update(expense)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update tax expense:', error);
      throw error;
    }

    return this.mapTaxExpense(data);
  }

  async deleteTaxExpense(id: number): Promise<void> {
    const userId = await this.waitForAuth();

    const { error } = await supabase
      .from('tax_expenses')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Failed to delete tax expense:', error);
      throw error;
    }
  }

  // Utility methods
  async close(): Promise<void> {
    // Supabase client doesn't need explicit closing
    return Promise.resolve();
  }

  async clearAllData(): Promise<void> {
    const { error: entriesError } = await supabase
      .from('time_entries')
      .delete()
      .neq('id', 0); // Delete all rows

    if (entriesError) {
      console.error('Failed to clear time entries:', entriesError);
      throw entriesError;
    }

    const { error: ratesError } = await supabase
      .from('monthly_rates')
      .delete()
      .neq('id', 0); // Delete all rows

    if (ratesError) {
      console.error('Failed to clear monthly rates:', ratesError);
      throw ratesError;
    }
  }

  async exportData(): Promise<{ timeEntries: TimeEntry[], monthlyRates: MonthlyRate[] }> {
    const { data: timeEntries, error: entriesError } = await supabase
      .from('time_entries')
      .select('*');

    if (entriesError) {
      console.error('Failed to export time entries:', entriesError);
      throw entriesError;
    }

    const { data: monthlyRates, error: ratesError } = await supabase
      .from('monthly_rates')
      .select('*');

    if (ratesError) {
      console.error('Failed to export monthly rates:', ratesError);
      throw ratesError;
    }

    return {
      timeEntries: timeEntries.map(row => {
        // Convert UTC times back to London times for export
        const shiftsForExport = row.shifts.map((shift: any) => {
          // Handle both old format (without UTC) and new format (with UTC)
          if (shift.clockInUTC && shift.clockOutUTC) {
            return {
              ...shift,
              clockIn: convertUTCToLondonTime(shift.clockInUTC),
              clockOut: convertUTCToLondonTime(shift.clockOutUTC)
            };
          }
          // For existing data without UTC conversion, return as-is
          return shift;
        });

        return {
          id: row.id,
          date: row.date,
          shifts: shiftsForExport,
          totalHours: row.total_hours,
          created_at: row.created_at,
          updated_at: row.updated_at
        };
      }),
      monthlyRates: monthlyRates.map(row => ({
        id: row.id,
        year: row.year,
        month: row.month,
        hourlyRate: row.hourly_rate,
        created_at: row.created_at,
        updated_at: row.updated_at
      }))
    };
  }

  async importData(data: { timeEntries: TimeEntry[], monthlyRates: MonthlyRate[] }): Promise<void> {
    await this.clearAllData();

    if (data.timeEntries.length > 0) {
      const { error: entriesError } = await supabase
        .from('time_entries')
        .insert(data.timeEntries.map(entry => {
          // Convert London times to UTC for storage
          const dateObj = parseDateString(entry.date);
          const shiftsForStorage = entry.shifts.map(shift => ({
            ...shift,
            clockInUTC: convertLondonTimeToUTC(shift.clockIn, dateObj),
            clockOutUTC: convertLondonTimeToUTC(shift.clockOut, dateObj)
          }));

          return {
            date: entry.date,
            shifts: shiftsForStorage,
            total_hours: entry.totalHours
          };
        }));

      if (entriesError) {
        console.error('Failed to import time entries:', entriesError);
        throw entriesError;
      }
    }

    if (data.monthlyRates.length > 0) {
      const { error: ratesError } = await supabase
        .from('monthly_rates')
        .insert(data.monthlyRates.map(rate => ({
          year: rate.year,
          month: rate.month,
          hourly_rate: rate.hourlyRate
        })));

      if (ratesError) {
        console.error('Failed to import monthly rates:', ratesError);
        throw ratesError;
      }
    }
  }
}

// Export a singleton instance
let supabaseServiceInstance: SupabaseService | null = null;

export const getSupabaseService = (): SupabaseService => {
  if (!supabaseServiceInstance) {
    supabaseServiceInstance = new SupabaseService();
  }
  return supabaseServiceInstance;
};
